<template>
  <div class="main">
    <div class="body-content">
      <!-- Modal-style delete confirmation -->
      <div v-if="vcnToDelete" class="modal-overlay confirm-modal">
        <div class="modal-content small" role="dialog" aria-modal="true">
          <div class="modal-header">
            <h3 style="margin:0;">Confirm Delete</h3>
            <button class="close-btn" @click="cancelDeleteVcn">&times;</button>
          </div>
          <div class="modal-body">
            Are you sure you want to delete VCN "{{ vcnToDelete.vcnName }}"?
          </div>
          <div class="modal-footer">
            <button class="cancel-button" @click="cancelDeleteVcn">Cancel</button>
            <button class="save-button" @click="confirmDeleteVcn">Delete</button>
          </div>
        </div>
      </div>
      <!-- upload files modal -->
      <upload-files-modal
        v-if="ShowUploadFileModal"
        :vcn="SelectedVcnForUpload"
        @close="closeUploadFileModal" />
      <div v-if="isLoading">Loading Vcn list...</div>
      <div v-else-if="error">Error loading Vcn list: {{ error }}</div>
      <div v-else>
        <div class="controls">
          <div class="search-container">
            <input v-model="searchQuery" placeholder="Search..." />
            <i class="fas fa-search search-icon"></i>
          </div>
          <h1 class="title">Vcn List</h1>
          <div class="actions">
            <div class="tooltip-container" @mouseenter="showTooltip" @mouseleave="hideTooltip">
              <button v-if="perm.canEdit" class="add-button" @click="openAddOverlay" title="Add New Container">
                <i class="fas fa-plus"></i>
              </button>
            </div>
            <div class="export-container">
              <button class="export-button" @click="toggleExportOptions" title="Export">
                <i class="fas fa-ellipsis-v"></i>
              </button>
              <ul v-if="showExportOptions" class="export-options">
                <li @click="exportToCSV">Export to CSV</li>
                <li @click="exportToPDF">Export to PDF</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Add Modal -->
        <div v-if="showAddModal" class="modal-overlay">
          <div class="modal-content small">
            <div class="modal-header">
              <h3 style="margin: 0;">Add VCN</h3>
              <button class="close-btn" @click="cancelAdd">&times;</button>
            </div>
            <div class="modal-body">
              <div class="edit-grid">
                <div class="form-group full">
                  <label>VCN Name</label>
                  <input v-model="newVcn.vcnName" placeholder="VCN Name *" />
                </div>
                <div class="form-group full">
                  <label>Customs VCN</label>
                  <input v-model="newVcn.custom_vcn" placeholder="VCN Name *" />
                </div>
                <div class="form-group">
                  <label>Vessel</label>
                  <v-select :clearable="false" v-model="newVcn.vesselID" :options="vesselNames" label="Name"
                    :reduce="vessel => vessel.Vesselid" placeholder="Vessel Name *" selectOnTab="True" />
                </div>
                <div class="form-group">
                  <label>Import Voyage</label>
                  <v-select :clearable="false" v-model="newVcn.import_voyage" :options="importVoyages" label="Name"
                    :reduce="voyage => voyage.Voyageid" placeholder="Import Voyage" selectOnTab="True" />
                </div>
                <div class="form-group">
                  <label>Export Voyage</label>
                  <v-select :clearable="false" v-model="newVcn.export_voyage" :options="importVoyages" label="Name"
                    :reduce="voyage => voyage.Voyageid" placeholder="Export Voyage" selectOnTab="True" />
                </div>
                <div class="form-group">
                  <label>Area</label>
                  <v-select :clearable="false" v-model="newVcn.MlocID" :options="Area" label="Name"
                    :reduce="Area => Area.locationid" placeholder="Area" selectOnTab="True" />
                </div>
                <div class="form-group">
                  <label>Imp Rot No</label>
                  <input v-model="newVcn.ImpRotNo" />
                </div>
                <div class="form-group">
                  <label>Imp Rot Date</label>
                  <input type="text" v-model="newVcn.ImpRotDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Exp Rot No</label>
                  <input v-model="newVcn.ExpRotNo" />
                </div>
                <div class="form-group">
                  <label>Exp Rot Date</label>
                  <input type="text" v-model="newVcn.ExpRotDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Berth Date</label>
                  <input type="text" v-model="newVcn.BerthDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Egm Date</label>
                  <input type="text" v-model="newVcn.EgmDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group inline-row">
                  <label class="inline-label">Is Sail</label>
                  <input type="checkbox" v-model="newVcn.IsSail" />
                  <label class="inline-label sail-label">Sail Date</label>
                  <input type="text" v-model="newVcn.SailDate" :disabled="!newVcn.IsSail" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Berth Location</label>
                  <v-select :clearable="false" v-model="newVcn.vesselLocation" :options="masterLocations" label="Name"
                    :reduce="loc => loc.locationid" selectOnTab="True" />
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="cancel-button" @click="cancelAdd" :disabled="isSubmitting">Cancel</button>
              <button class="save-button" @click="saveVcn" :disabled="isSubmitting">Save</button>
            </div>
          </div>
        </div>

        <!-- Edit Modal -->
        <div v-if="showEditModal" class="modal-overlay">
          <div class="modal-content small">
            <div class="modal-header">
              <h3 style="margin: 0;">Edit VCN</h3>
              <button class="close-btn" @click="cancelEdit">&times;</button>
            </div>
            <div class="modal-body">
              <div class="edit-grid">
                <div class="form-group full">
                  <label>VCN Name</label>
                  <input v-model="newVcn.vcnName" placeholder="VCN Name *" />
                </div>
                <div class="form-group full">
                  <label>Customs VCN</label>
                  <input v-model="newVcn.custom_vcn" placeholder="VCN Name *" />
                </div>
                <div class="form-group">
                  <label>Vessel</label>
                  <v-select :clearable="false" v-model="newVcn.vesselID" :options="vesselNames" label="Name"
                    :reduce="vessel => vessel.Vesselid" placeholder="Vessel Name *" />
                </div>
                <div class="form-group">
                  <label>Import Voyage</label>
                  <v-select :clearable="false" v-model="newVcn.import_voyage" :options="importVoyages" label="Name"
                    :reduce="voyage => voyage.Voyageid" placeholder="Import Voyage" />
                </div>
                <div class="form-group">
                  <label>Export Voyage</label>
                  <v-select :clearable="false" v-model="newVcn.export_voyage" :options="importVoyages" label="Name"
                    :reduce="voyage => voyage.Voyageid" placeholder="Export Voyage" />
                </div>
                <div class="form-group">
                  <label>Area</label>
                  <v-select :clearable="false" v-model="newVcn.MlocID" :options="Area" label="Name"
                    :reduce="Area => Area.locationid" placeholder="Area" />
                </div>
                <div class="form-group">
                  <label>Imp Rot No</label>
                  <input v-model="newVcn.ImpRotNo" />
                </div>
                <div class="form-group">
                  <label>Imp Rot Date</label>
                  <input type="text" v-model="newVcn.ImpRotDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Exp Rot No</label>
                  <input v-model="newVcn.ExpRotNo" />
                </div>
                <div class="form-group">
                  <label>Exp Rot Date</label>
                  <input type="text" v-model="newVcn.ExpRotDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Berth Date</label>
                  <input type="text" v-model="newVcn.BerthDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Egm Date</label>
                  <input type="text" v-model="newVcn.EgmDate" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group inline-row">
                  <label  class="inline-label">Is Sail</label>
                  <input type="checkbox" v-model="newVcn.IsSail" />
                  <label class="inline-label">Sail Date</label>
                  <input type="text" v-model="newVcn.SailDate" :disabled="!newVcn.IsSail" placeholder="dd/mm/yyyy" @blur="validateDate()" />
                </div>
                <div class="form-group">
                  <label>Berth Location</label>
                  <v-select :clearable="false" v-model="newVcn.vesselLocation" :options="masterLocations" label="Name"
                    :reduce="loc => loc.locationid" />
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="cancel-button" @click="cancelEdit" :disabled="isSubmitting">Cancel</button>
              <button class="save-button" @click="saveVcn" :disabled="isSubmitting">Save</button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="vcn-table table-design">
            <thead>
              <tr>
                <th>
                  <span>Port Vcn</span>
                </th>
                <th>
                  <span>Vessel Name</span>
                </th>
                <th>
                  <span>Import Voyage Name</span>
                </th>
                <th>
                  <span>Export Voyage Name</span>
                </th>
                <th>
                  <span>Area</span>
                </th>
                <th>
                  <span>Imp Rot No</span>
                </th>
                <th>
                  <span>Imp Rot Date</span>
                </th>
                <th>
                  <span>Exp Rot No</span>
                </th>
                <th>
                  <span>Exp Rot Date</span>
                </th>
                <th>
                  <span>Berth Date</span>
                </th>
                <th>
                  <span>Egm Date</span>
                </th>
                <th>
                  <span>Is Sail</span>
                </th>
                <th>
                  <span>Sail Date</span>
                </th>
                <th>
                  <span>Berth Location</span>
                </th>
                <th v-if="hasPermissions">Actions</th>
                <th v-if="hasPermissions">Upload</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(vcn) in paginatedVcns" :key="vcn.vcnID">
                <td>{{ vcn.vcnName }}</td>
                <td>{{ vcn.VesselName }}</td>
                <td>{{ vcn.import_voyageName }}</td>
                <td>{{ vcn.export_voyageName }}</td>
                <td>{{ vcn.area }}</td>
                <td>{{ vcn.ImpRotNo }}</td>
                <td>{{ formatDate(vcn.ImpRotDate) }}</td>
                <td>{{ vcn.ExpRotNo }}</td>
                <td>{{ formatDate(vcn.ExpRotDate) }}</td>
                <td>{{ formatDate(vcn.BerthDate) }}</td>
                <td>{{ formatDate(vcn.EgmDate) }}</td>
                <td>{{ vcn.IsSail ? 'Yes' : 'No' }}</td>
                <td>{{ formatDate(vcn.SailDate) }}</td>
                <td>{{ getLocationNameById(vcn.vesselLocation) }}</td>
                <td v-if="hasPermissions">
                  <button v-if="perm.canEdit" @click="editVcn(vcn)" class="edit-button" :disabled="isSubmitting"><i class="fas fa-edit"></i></button>
                  <button v-if="perm.canDelete" @click="deleteVcn(vcn)" class="cancel-button" :disabled="isSubmitting"><i class="fas fa-trash"></i></button>
                </td>
                <td v-if="hasPermissions">
                  <button class="edit-button" style="margin-left:8px; background:#198754; color: #fff;"
                    @click="ShowUploadFileList(vcn)">Upload Files</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="bottom-controls">
          <div class="rows-per-table">
            <button @mouseenter="showTooltip" @mouseleave="hideTooltip" v-for="option in [10, 20, 30]" :key="option"
              :class="{ 'active': rowsPerPage === option }" @click="rowsPerPage = option" title="Rows Per Table">
              {{ option }}
            </button>
          </div>
          <div class="pagination">
            <button @click="goToPage(currentPage - 1)" :disabled="!showPrevious"> &lt; </button>
            <button v-for="page in paginatedPages" :key="page" @click="goToPage(page)"
              :class="{ active: page === currentPage }">
              {{ page }}
            </button>
            <button @click="goToPage(currentPage + 1)" :disabled="!showNext"> &gt; </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { useToast } from 'vue-toastification';
import { hasPermission } from '../utils/permissions';
import * as cs from '../assets/js/commonscript.js'
import jsPDF from 'jspdf';
import UploadFilesModal from '../components/UploadFilesModal.vue';
export default {
  components: {
    VSelect,
    UploadFilesModal,
  },
  data() {
    return {
      isSubmitting: false,
      vesselNames: JSON.parse(localStorage.getItem('vessel')),
      masterLocations: JSON.parse(localStorage.getItem('locations')),
      locationMap: {},
      Area: [],
      vcns: [],
      nameError: '',
      vesselError: '',
      areaError: '',
      voyageError: '',
      importVoyages: JSON.parse(localStorage.getItem('voyage')),
      isLoading: false,
      error: null,
      showExportOptions: false,
      tooltipVisible: false,
      tooltipTimeout: null,
      hasPermissions: false,
      perm: {
        canView: false,
        canEdit: false,
        canDelete: false,
      },
      vcnToDelete: null,
      searchQuery: '',
      rowsPerPageOptions: [10, 20, 30],
      rowsPerPage: 10,
      currentPage: 1,
      isAddingNew: false,
      isEditing: false,
      selectedVcn: null,
      showAddModal: false,
      showEditModal: false,
      // upload modal state
      ShowUploadFileModal: false,
      SelectedVcnForUpload: null,
      newVcn: {
        vcnNo: '',
        vcnName: '',
        custom_vcn: '',
        vesselID: 0,
        VesselName: '',
        import_voyage: 0,
        import_voyageName: '',
        export_voyage: 0,
        export_voyageName: '',
        MlocID: 0,
        area: '',
        ImpRotNo: '',
        ImpRotDate: null,
        ExpRotNo: '',
        ExpRotDate: null,
        BerthDate: null,
        EgmDate: null,
        IsSail: false,
        SailDate: null,
        vesselLocation: 0,
        Vesselarea: 0,
      },
      insertvcn: {
        vcnName: '',
        custom_vcn: '',
        vesselID: null,
        import_voyage: null,
        export_voyage: null,
        MlocID: null,
        ImpRotNo: '',
        ImpRotDate: null,
        ExpRotNo: '',
        ExpRotDate: null,
        BerthDate: null,
        EgmDate: null,
        IsSail: false,
        SailDate: null,
        vesselLocation: null,
      },
      filters: {
        vcnID: '',
        vcnName: '',
        custom_vcn: '',
        vesselID: '',
        VesselName: '',
        import_voyage: '',
        import_voyageName: '',
        export_voyage: '',
        export_voyageName: '',
        MlocID: '',
        area: '',
        ImpRotNo: '',
        ImpRotDate: null,
        ExpRotNo: '',
        ExpRotDate: null,
        BerthDate: null,
        EgmDate: null,
        IsSail: '',
        SailDate: null,
        vesselLocation: '',
        Vesselarea: '',
      },
    };
  },
  watch: {
    'newVcn.vcnName'(newName) {
      if (this.isAddingNew || (this.isEditing && this.newVcn.vcnName !== this.selectedVcn.vcnName)) {
        this.checkVcnName(newName);
      }
    },
    'newVcn.vesselID'(newVesselID) {
      // Clear vessel error when a vessel is selected
      if (newVesselID && newVesselID !== 0) {
        this.vesselError = '';
      }
    },
    'newVcn.MlocID'(newMlocID) {
      // Clear area error when an area is selected
      if (newMlocID && newMlocID !== 0) {
        this.areaError = '';
      }
    },
    'newVcn.import_voyage'(newImportVoyage) {
      // Clear voyage error when import voyage is selected
      if ((newImportVoyage && newImportVoyage !== 0) || (this.newVcn.export_voyage && this.newVcn.export_voyage !== 0)) {
        this.voyageError = '';
      }
    },
    'newVcn.export_voyage'(newExportVoyage) {
      // Clear voyage error when export voyage is selected
      if ((newExportVoyage && newExportVoyage !== 0) || (this.newVcn.import_voyage && this.newVcn.import_voyage !== 0)) {
        this.voyageError = '';
      }
    },
    searchQuery() {
      // Reset to the first page when search query changes
      this.currentPage = 1;
    },
  },
  created() {
    this.fetchVcns();
    this.checkPermissions();
    this.fetchMasterLocations();
    //let keysPressed = {};
    //document.addEventListener('keydown', (e) => {
    //  keysPressed[e.key] = true;
    //  if (keysPressed['Alt'] && e.key == 's') {
    //    this.saveVcn()
    //  }
    //  document.addEventListener('keyup', (e) => {
    //    delete keysPressed[e.key];
    //  });
    //});
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
    // removed formattedImpRotDate computed in favor of plain text inputs bound to newVcn.*
    minDate() {
      return `${this.currentYear}-01-01`;
    },
    filteredVcns() {
      return this.vcns
        .filter((vcn) => {
          return Object.keys(this.filters).every((key) => {
            return !this.filters[key] || String(vcn[key]).toLowerCase().includes(this.filters[key].toLowerCase());
          }) && Object.values(vcn).some((value) =>
            String(value).toLowerCase().includes(this.searchQuery.toLowerCase())
          );
        });
    },
    paginatedVcns() {
      const start = (this.currentPage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      return this.filteredVcns.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredVcns.length / this.rowsPerPage);
    },
    hasPrevPage() {
      return this.currentPage > 1;
    },
    hasNextPage() {
      return this.currentPage < this.totalPages;
    },
    paginatedVessels() {
      const start = (this.currentPage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      return this.filteredVessels.slice(start, end);
    },
    paginatedPages() {
      const totalPages = this.totalPages;
      const currentPage = this.currentPage; // assuming you have a currentPage data property
      const maxPagesToShow = 10;
      let pages = [];

      if (totalPages <= maxPagesToShow) {
        // Show all pages if they are fewer than the maxPagesToShow
        pages = Array.from({ length: totalPages }, (_, i) => i + 1);
      } else {
        // Always show the first page
        pages.push(1);

        // Show a range around the current page
        let start = Math.max(2, currentPage - 4);
        let end = Math.min(totalPages - 1, currentPage + 4);

        if (start > 2) {
          pages.push('...');
        }

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (end < totalPages - 1) {
          pages.push('...');
        }

        // Always show the last page
        if (totalPages > 1) {
          pages.push(totalPages);
        }
      }

      return pages;
    },
    showPrevious() {
      return this.currentPage > 1;
    },
    showNext() {
      return this.currentPage < this.totalPages;
    }
  },
  methods: {
    getLocationNameById(locationId) {
      const location = this.masterLocations.find(loc => loc.locationid === locationId);
      return location ? location.Name : 'Unknown';
    },
    goToPage(page) {
      if (page === '...' || page === this.currentPage) return;
      this.currentPage = page;
    },
    async fetchMasterLocations() {
      this.isLoading = true;
      this.error = null;

      const token = localStorage.getItem('access_token');

      if (!token) {
        this.error = 'No token found in localStorage';
        this.isLoading = false;
        return;
      }

      try {
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const response = await axios.get('/master_location_list/', config);
        this.Area = response.data[2];
      } catch (error) {
        console.error('Error fetching Master Locations:', error);
        this.error = 'Error fetching Master Locations. Check console for details.';
      } finally {
        this.isLoading = false;
      }
    },
    formatDateToDDMMYYYY(dateStr) {
      const [year, month, day] = dateStr.split('-');
      return `${day}-${month}-${year}`;
    },
    formatDateToYYYYMMDD(dateStr) {
      const [day, month, year] = dateStr.split('-');
      return `${year}-${month}-${day}`;
    },
    // Removed updateImpRotDate: inputs now bind directly to newVcn.* and use validateDate() on blur
    async fetchVcns() {
      this.isLoading = true;
      this.error = null;

      const token = localStorage.getItem('access_token');

      if (!token) {
        this.error = 'No token found in localStorage';
        this.isLoading = false;
        return;
      }

      try {
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const response = await axios.get('/vcn_list/', config);
        this.vcns = response.data[2].map(vcn => ({
          ...vcn,
        }));
        console.log('VCNs:', this.vcns);
      } catch (error) {
        console.error('Error fetching VCN list:', error);
        this.error = 'Error fetching VCN list. Check console for details.';
      } finally {
        this.isLoading = false;
      }
    },

    async checkVcnName(name) {
      // Check if name is empty (mandatory field)
      if (name.trim() === '') {
        this.nameError = 'VCN name is required.';
        return;
      }

      try {
        const vcnExists = this.vcns.some(vcn => vcn.vcnName.toLowerCase() === name.toLowerCase());
        if (vcnExists) {
          this.nameError = 'VCN name already exists.';
          useToast().error('VCN name already exists.');
        } else {
          this.nameError = '';
        }
      } catch (err) {
        console.error('Error checking VCN name:', err);
      }
    },

    formatDate(dateString) {
      if (!dateString) {
        return ''; // Or return 'N/A' or any other placeholder
      }
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    validateDate() {
      const toast = useToast();
      const fields = [
        { key: 'ImpRotDate', label: 'Imp Rot Date' },
        { key: 'ExpRotDate', label: 'Exp Rot Date' },
        { key: 'BerthDate', label: 'Berth Date' },
        { key: 'EgmDate', label: 'Egm Date' },
        { key: 'SailDate', label: 'Sail Date' },
      ];

      fields.forEach(f => {
        const val = this.newVcn[f.key];
        if (val && String(val).trim() !== '') {
          let checkVal = String(val).trim();
          // If in ISO format YYYY-MM-DD, convert to dd/mm/yyyy for validation
          if (/^\d{4}-\d{2}-\d{2}$/.test(checkVal)) {
            const [y, m, d] = checkVal.split('-');
            checkVal = `${d}/${m}/${y}`;
          } else {
            // Normalize hyphens to slashes (dd-mm-yyyy -> dd/mm/yyyy)
            checkVal = checkVal.replace(/-/g, '/');
          }

          if (cs.isvalidDate(checkVal) === false) {
            toast.error(`Invalid ${f.label}. Use dd/mm/yyyy`);
          }
        }
      });
    },

    validateVcnName() {
      if (this.vcns.includes(this.newVcn.vcnName)) {
        this.vcnNameError = 'VCN Name must be unique.';
      } else {
        this.vcnNameError = '';
      }
    },

    checkPermissions() {
      const actionKey = 'vcn';
      const canView = hasPermission(actionKey, 'view');
      const canEdit = hasPermission(actionKey, 'edit');
      const canDelete = hasPermission(actionKey, 'delete');

      this.perm = { canView, canEdit, canDelete };
      // show Actions column when edit or delete permitted
      this.hasPermissions = !!(canEdit || canDelete);
    },

    openAddOverlay() {
      this.showAddModal = true;
      this.isEditing = false;
      this.selectedVcn = null;
      this.resetNewVcn();
    },

    cancelAdd() {
      this.showAddModal = false;
      this.isAddingNew = false;
      this.isEditing = false;
      this.selectedVcn = null;
      this.resetNewVcn();
    },
    // show upload modal for selected vcn
    ShowUploadFileList(vcn) {
      this.ShowUploadFileModal = true;
      this.SelectedVcnForUpload = vcn;
    },
    closeUploadFileModal() {
      this.ShowUploadFileModal = false;
      this.SelectedVcnForUpload = null;
    },
    formatDateFromDDMMYYYY(dateStr) {
      const [day, month, year] = dateStr.split('-');
      return `${year}-${month}-${day}`;
    },
    async saveVcn() {
      // Prevent multiple submissions
      if (this.isSubmitting) {
        return;
      }
      
      const toast = useToast();
      
      // Check if VCN name is empty (mandatory field)
      if (!this.newVcn.vcnName || this.newVcn.vcnName.trim() === '') {
        this.nameError = 'VCN name is required.';
        toast.error('VCN name is required.');
        return;
      }
      
      // Check if vessel is selected (mandatory field)
      if (!this.newVcn.vesselID || this.newVcn.vesselID === 0) {
        this.vesselError = 'Vessel selection is required.';
        toast.error('Vessel selection is required.');
        return;
      } else {
        this.vesselError = '';
      }
      
      // Check if area is selected (mandatory field)
      if (!this.newVcn.MlocID || this.newVcn.MlocID === 0) {
        this.areaError = 'Area selection is required.';
        toast.error('Area selection is required.');
        return;
      } else {
        this.areaError = '';
      }
      
      // Check if at least one voyage is selected (mandatory field)
      if ((!this.newVcn.import_voyage || this.newVcn.import_voyage === 0) && (!this.newVcn.export_voyage || this.newVcn.export_voyage === 0)) {
        this.voyageError = 'At least one voyage (Import or Export) must be selected.';
        toast.error('At least one voyage (Import or Export) must be selected.');
        return;
      } else {
        this.voyageError = '';
      }
      
      if (this.nameError !== '' || this.vesselError !== '' || this.areaError !== '' || this.voyageError !== '') {
        toast.error('Please fix the errors before saving.');
        return;
      }
      
      // Set loading state
      this.isSubmitting = true;
      
      try {
        const token = localStorage.getItem('access_token');

        if (!token) {
          console.error('No token found in localStorage');
          return;
        }

        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };
        const formattedVcn = { ...this.newVcn };
        // normalize date fields to ISO (YYYY-MM-DD) before sending to API
        const toISO = (val) => {
          if (!val) return null;
          let s = String(val).trim();
          if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // already ISO
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return cs.ConvertToISODate(s); // dd/mm/yyyy -> yyyy-mm-dd
          if (/^\d{2}-\d{2}-\d{4}$/.test(s)) { const [d,m,y] = s.split('-'); return `${y}-${m}-${d}`; }
          // fallback: replace hyphens with slashes and try convert
          try { return cs.ConvertToISODate(s.replace(/-/g, '/')); } catch (e) { return s; }
        };

        formattedVcn.ImpRotDate = toISO(formattedVcn.ImpRotDate);
        formattedVcn.ExpRotDate = toISO(formattedVcn.ExpRotDate);
        formattedVcn.BerthDate = toISO(formattedVcn.BerthDate);
        formattedVcn.EgmDate = toISO(formattedVcn.EgmDate);
        formattedVcn.SailDate = toISO(formattedVcn.SailDate);
        console.log(formattedVcn)
        if (this.isEditing) {
          await axios.put(`/update_vcn/${this.selectedVcn.vcnID}/`, formattedVcn, config);
          toast.success('VCN updated successfully.');
          this.resetNewVcn();
          // close edit modal
          this.showEditModal = false;
          this.selectedVcn = null;
          this.isEditing = false;
        } else {
          const formVcn = { ...this.insertvcn };
          formVcn.vcnName = this.newVcn.vcnName;
          formVcn.vesselID = this.newVcn.vesselID;
          formVcn.import_voyage = this.newVcn.import_voyage;
          formVcn.export_voyage = this.newVcn.export_voyage;
          formVcn.MlocID = this.newVcn.MlocID;
          formVcn.ImpRotNo = this.newVcn.ImpRotNo;
          formVcn.ImpRotDate = toISO(this.newVcn.ImpRotDate);
          formVcn.ExpRotNo = this.newVcn.ExpRotNo;
          formVcn.ExpRotDate = toISO(this.newVcn.ExpRotDate);
          formVcn.BerthDate = toISO(this.newVcn.BerthDate);
          formVcn.EgmDate = toISO(this.newVcn.EgmDate);
          formVcn.IsSail = this.newVcn.IsSail;
          formVcn.SailDate = this.newVcn.SailDate;
          formVcn.vesselLocation = this.newVcn.vesselLocation;

          await axios.post('/create_vcn/', formVcn, config);
          toast.success('VCN added successfully.');
          this.resetNewVcn();
          // close add modal
          this.showAddModal = false;
        }

        await this.fetchVcns();
      } catch (error) {
        console.error('Error saving VCN:', error);
        toast.error('Error saving VCN.');
      } finally {
        // Always reset loading state
        this.isSubmitting = false;
      }
    },
    toggleExportOptions() {
      this.showExportOptions = !this.showExportOptions;
      //console.log(this.showExportOptions);
      document.addEventListener('click', this.handleOutsideClick);
    },
    handleOutsideClick(event) {
      if (!event.target.closest('.export-container')) {
        this.showExportOptions = false;
        document.removeEventListener('click', this.handleOutsideClick);
      }
    },
    editVcn(vcn) {
      this.selectedVcn = vcn;
      this.newVcn = { ...vcn };
      this.newVcn.ImpRotDate = this.newVcn.ImpRotDate ? this.newVcn.ImpRotDate.substring(0, 10) : null;
      this.newVcn.ExpRotDate = this.newVcn.ExpRotDate ? this.newVcn.ExpRotDate.substring(0, 10) : null;
      this.newVcn.BerthDate = this.newVcn.BerthDate ? this.newVcn.BerthDate.substring(0, 10) : null;
      this.newVcn.EgmDate = this.newVcn.EgmDate ? this.newVcn.EgmDate.substring(0, 10) : null;
      this.newVcn.SailDate = this.newVcn.SailDate ? this.newVcn.SailDate.substring(0, 10) : null;
      this.isEditing = true;
      this.isAddingNew = false;
      this.showEditModal = true;
    },

    cancelEdit() {
      this.showEditModal = false;
      this.isAddingNew = false;
      this.isEditing = false;
      this.selectedVcn = null;
      this.resetNewVcn();
    },

    deleteVcn(vcn) {
      // show toast-style confirmation
      this.vcnToDelete = vcn;
    },

    cancelDeleteVcn() {
      this.vcnToDelete = null;
    },

    confirmDeleteVcn() {
      if (!this.vcnToDelete) return;
      const toDelete = this.vcnToDelete;
      this.vcnToDelete = null;
      this.removeVcn(toDelete);
    },

    async removeVcn(vcn) {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          console.error('No token found in localStorage');
          return;
        }
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };
        await axios.get('/delete_vcn?vcnid='+vcn.vcnID, config);
        
        this.fetchVcns();
        const toast = useToast();
        toast.success('VCN deleted successfully.');
      } catch (error) {
        console.error('Error deleting VCN:', error);
        const toast = useToast();
        toast.error('Error deleting VCN.');
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handleRowsPerPageChange(rowsPerPage) {
      this.rowsPerPage = rowsPerPage;
      this.currentPage = 1;
    },
    resetNewVcn() {
      this.nameError = '';
      this.vesselError = '';
      this.areaError = '';
      this.voyageError = '';
      this.newVcn = {
        vcnNo: '',
        vcnName: '',
        vesselID: null,
        VesselName: '',
        import_voyage: null,
        import_voyageName: '',
        export_voyage: null,
        export_voyageName: '',
        MlocID: null,
        area: '',
        ImpRotNo: '',
        ImpRotDate: null,
        ExpRotNo: '',
        ExpRotDate: null,
        BerthDate: null,
        EgmDate: '',
        IsSail: false,
        SailDate: null,
        vesselLocation: null,
        Vesselarea: null,
      };
    },
    exportToCSV() {
      const csvContent = this.paginatedVcns.map((vcn) => {
        return [
          vcn.vcnName,
          vcn.VesselName,
          vcn.import_voyageName,
          vcn.export_voyageName,
          vcn.area,
          vcn.ImpRotNo,
          vcn.ImpRotDate,
          vcn.ExpRotNo,
          vcn.ExpRotDate,
          vcn.BerthDate,
          vcn.EgmDate,
          vcn.IsSail ? 'Yes' : 'No',
          vcn.SailDate,
          //this.locationMap[vcn.vesselLocation],
        ].join(',');
      }).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'vcn_list.csv';
      link.click();
    },

    exportToPDF() {
      // Create a new jsPDF instance
      const doc = new jsPDF();
      const header = 'Exported VCN Data';

      // Set the font size for the header
      doc.setFontSize(16);
      doc.text(header, 10, 10);

      // Set the font size for the body text
      doc.setFontSize(12);
      let yOffset = 20;

      // Check if vcns is defined and is an array
      if (Array.isArray(this.vcns) && this.vcns.length > 0) {
        // Loop through each item in vcns
        this.vcns.forEach(item => {
          // Format the text for each VCN entry
          const text = `VCN: ${item.vcnName || 'N/A'}, Vessel: ${item.VesselName || 'N/A'}, Import Voyage: ${item.import_voyageName || 'N/A'}, Export Voyage: ${item.export_voyageName || 'N/A'}, Status: ${item.IsSail ? 'Sailed' : 'Not Sailed'}`;
          doc.text(text, 10, yOffset);
          yOffset += 10; // Increment yOffset for the next line
        });
      } else {
        // If no data is available, notify in the PDF
        doc.text('No VCN data available to export.', 10, yOffset);
      }

      // Save the PDF with a specific filename
      doc.save('vcn_list.pdf');

      // Close the export options dropdown
      this.showExportOptions = false;
    }
  },
};
</script>

<style scoped>
input, select {
    padding: 2px 2px;
    font-size: 14px;
    margin: 5px 0;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 110px;
}

.save-button:disabled,
.cancel-button:disabled,
.edit-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.save-button:disabled:hover,
.cancel-button:disabled:hover,
.edit-button:disabled:hover {
  background-color: #6c757d;
}

input[required] {
  border-left: 3px solid #007bff;
}

input[required]:invalid {
  border-left: 3px solid #dc3545;
}

.v-select.vs--single[required] .vs__dropdown-toggle {
  border-left: 3px solid #007bff;
}
/* Modal styles reused from other components */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.modal-content.small {
  /* Increased width for better desktop layout; still caps with max-width for small screens */
  width: 1100px;
  max-width: 98%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  /* Make the modal a column flex container so header/footer stay fixed
     and the body can scroll when content grows. Limit height to viewport. */
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}
.modal-header { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-bottom:1px solid #eee; }
.modal-body { padding:12px 16px; overflow-y: auto; flex: 1 1 auto; }
.modal-footer { padding:12px 16px; border-top:1px solid #eee; text-align:right; }
.edit-grid { display:grid; grid-template-columns: repeat(4, 1fr); gap:5px 16px; }
.edit-grid .form-group { display:flex; flex-direction:column; }

/* Inline row for related controls (checkbox + date) */
.edit-grid .form-group.inline-row { flex-direction:row; align-items:center; gap:8px; }
.edit-grid .form-group.inline-row .inline-label { margin-bottom:0; margin-right:6px; font-weight:600; }
.edit-grid .form-group.inline-row input[type="checkbox"] { width: auto; margin-right:6px; }
.edit-grid .form-group.inline-row input[type="text"] { flex: 1 1 auto; min-width:120px; }

/* Ensure inputs and selects in the grid fill their column space */
.edit-grid input,
.edit-grid select,
.edit-grid .v-select,
.edit-grid .vs__dropdown-toggle {
  width: 100%;
  box-sizing: border-box;
}
.edit-grid .form-group.full { grid-column: span 2; }
.form-group label { font-weight:600; margin-bottom:6px; }
.modal-content input { padding:8px; border-radius:4px; border:1px solid #ccc; }

/* Confirmation modal tweaks */
.confirm-modal .modal-content.small { width: 420px; max-width: 95%; }
.confirm-modal .modal-body { padding: 18px; }
.confirm-modal .modal-footer { display: flex; justify-content: flex-end; gap: 8px; }

/* Confirm modal button styles */
.confirm-modal .cancel-button {
  background: #f1f1f1;
  border: 1px solid #ddd;
  color: #222;
  padding: 8px 12px;
  border-radius: 4px;
}
.confirm-modal .cancel-button:disabled { opacity: 0.6; }
.confirm-modal .save-button {
  background: #dc3545;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
}
.confirm-modal .save-button:disabled { opacity: 0.6; }

@media (max-width: 800px) {
  .edit-grid { grid-template-columns: 1fr; }
  /* On small screens make .full items span the single column (stacked) */
  .edit-grid .form-group.full { grid-column: 1 / -1; }
  /* Inline rows stack vertically on small screens */
  .edit-grid .form-group.inline-row { flex-direction: column; align-items: stretch; }
  .edit-grid .form-group.inline-row .inline-label { margin-bottom:6px; }
  .edit-grid .form-group.inline-row input[type="checkbox"] { margin-right:0; }
}
</style>

<!-- global toast z-index so toasts appear above modals/overlays -->
<style>
/* Raise toast container above modal overlays (modals use z-index:10000) */
.Vue-Toastification__container {
  z-index: 20000 !important;
}

/* Ensure top positioned toasts sit near the top of the viewport */
.Vue-Toastification__container.top-center,
.Vue-Toastification__container.top-right,
.Vue-Toastification__container.top-left {
  top: 8px !important;
}
</style>

